import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './RaiseTicketModal.css';

/**
 * Minimal Phase-1 "Need Help? / Raise Ticket" modal for logged-in users: one message box,
 * POST /support/tickets, confirmation with the ticket number. Identity/org come from the JWT
 * server-side. A per-open clientRequestId makes double-clicks and retries idempotent.
 */
function RaiseTicketModal({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [clientRequestId] = useState(() =>
    window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/support/tickets', {
        message: message.trim(),
        clientRequestId,
      });
      setTicketNumber(data?.ticketNumber || 'OK');
    } catch {
      setError(t('RaiseTicketModal.error', 'Could not submit your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sup-overlay" onClick={onClose}>
      <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sup-header">
          <h3>
            <i className="fas fa-life-ring" /> {t('RaiseTicketModal.title', 'Need help?')}
          </h3>
          <button className="sup-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        {ticketNumber ? (
          <div className="sup-body">
            <p className="sup-success">
              <i className="fas fa-check-circle" />{' '}
              {t('RaiseTicketModal.success', 'Your request has been logged and our support team has been notified.')}
            </p>
            {ticketNumber !== 'OK' && (
              <p className="sup-ticket-no">
                {t('RaiseTicketModal.ticketNo', 'Your ticket number is')} <strong>{ticketNumber}</strong>
              </p>
            )}
            <div className="sup-actions">
              <button className="sup-btn-cancel" onClick={() => { onClose(); navigate('/dashboard/support'); }}>
                {t('RaiseTicketModal.viewTickets', 'View my tickets')}
              </button>
              <button className="sup-btn-primary" onClick={onClose}>
                {t('RaiseTicketModal.done', 'Done')}
              </button>
            </div>
          </div>
        ) : (
          <form className="sup-body" onSubmit={handleSubmit}>
            {error && <p className="sup-error">{error}</p>}
            <p className="sup-hint">
              {t('RaiseTicketModal.hint', 'Describe the problem you are facing and our support team will get back to you.')}
            </p>
            <label htmlFor="sup-message">{t('RaiseTicketModal.message', 'Message')}</label>
            <textarea
              id="sup-message"
              rows={5}
              maxLength={5000}
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('RaiseTicketModal.messagePh', 'What do you need help with?')}
            />
            <p className="sup-hint">
              <button type="button" className="sup-link" onClick={() => { onClose(); navigate('/dashboard/support'); }}>
                {t('RaiseTicketModal.viewTickets', 'View my tickets')} →
              </button>
            </p>
            <div className="sup-actions">
              <button type="button" className="sup-btn-cancel" onClick={onClose}>
                {t('RaiseTicketModal.cancel', 'Cancel')}
              </button>
              <button type="submit" className="sup-btn-primary" disabled={submitting || !message.trim()}>
                {submitting
                  ? t('RaiseTicketModal.sending', 'Sending…')
                  : t('RaiseTicketModal.submit', 'Raise ticket')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default RaiseTicketModal;
